<script setup lang="ts">
import { computed } from 'vue';

import { IconAlert, IconUser } from '@/components/design/icons';
import { TaskStatus, type ContractManagementExtractionInDb } from '@/api';
import { formatDisplayValue } from '@/utils/displayAiResponse';

interface Props {
  /** The extraction object - null if extraction doesn't exist yet */
  extraction?: ContractManagementExtractionInDb | null;
  /** The cell value (formatted) */
  value?: unknown;
  /** Response type for formatting */
  responseType?: string | object;
  /** Whether this is a file node (for display formatting) */
  isFileNode?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  extraction: null,
  value: undefined,
  responseType: undefined,
  isFileNode: false,
});

/** Values match ContractManagementExtractionInDb['processing_state'] plus 'no-extraction' for null. */
type ExtractionDisplayState = 'no-extraction' | 'pending' | 'running' | 'failed' | 'done';

/**
 * Determine the extraction state for display.
 * Returns: 'no-extraction' | 'pending' | 'running' | 'failed' | 'done'
 */
const extractionState = computed((): ExtractionDisplayState => {
  if (props.extraction === null) {
    return 'no-extraction';
  }

  const state = props.extraction.processing_state;

  // Use backend values directly (ContractManagementExtractionInDb.processing_state union)
  if (
    state === TaskStatus.PENDING ||
    state === TaskStatus.RUNNING ||
    state === TaskStatus.FAILED ||
    state === TaskStatus.DONE
  ) {
    return state;
  }

  return 'no-extraction';
});

/** True when extraction has a human override with a value set (used for icon + showing value in pending/running). */
const hasHumanOverrideValue = computed(() => props.extraction?.human_overwrite != null);

/** When pending/running but user overrode with a value, show that value instead of spinner. */
const showOverrideValueNow = computed(
  () =>
    (extractionState.value === 'pending' || extractionState.value === 'running') &&
    hasHumanOverrideValue.value
);

/** True when we are displaying a value (done or override-in-progress). */
const isShowingValue = computed(
  () => extractionState.value === 'done' || showOverrideValueNow.value
);

/**
 * Get the display value for the cell.
 * Shown when extraction is done, or when pending/running but user set a human override value.
 */
const displayValue = computed(() => {
  if (!isShowingValue.value) return null;

  if (
    props.isFileNode &&
    (props.value === null || props.value === undefined || props.value === '')
  ) {
    return '—';
  }

  if (props.value != null) {
    return formatDisplayValue(props.value, props.responseType);
  }

  return '';
});

/**
 * Get the error message from extraction
 */
const errorMessage = computed(() => {
  if (extractionState.value === 'failed' && props.extraction?.error_text) {
    return props.extraction.error_text;
  }
  return null;
});
</script>

<template>
  <div class="extraction-cell-state">
    <!-- No extraction: show spinner + "Pending extraction" (same as pending state) -->
    <Transition name="fade" mode="out-in">
      <div
        v-if="extractionState === 'no-extraction'"
        key="no-extraction"
        class="extraction-state-container"
      >
        <NSpin :size="16" />
        <span class="extraction-state-text loading-text">Pending extraction</span>
      </div>

      <!-- Pending/in progress with human override: show override value (no spinner) -->
      <div
        v-else-if="showOverrideValueNow"
        key="override-value"
        class="extraction-state-container extraction-done extraction-with-override"
      >
        <span class="extraction-value">{{ displayValue || '' }}</span>
        <NTooltip v-if="hasHumanOverrideValue" trigger="hover" placement="top">
          <template #trigger>
            <NIcon :size="14" color="#2563eb" class="override-icon" aria-hidden="true">
              <IconUser />
            </NIcon>
          </template>
          <span>Manually overwritten</span>
        </NTooltip>
      </div>

      <!-- Pending: show spinner with text -->
      <div
        v-else-if="extractionState === 'pending'"
        key="pending"
        class="extraction-state-container"
      >
        <NSpin :size="16" />
        <span class="extraction-state-text loading-text">Extraction pending</span>
      </div>

      <!-- In progress: show spinner with text -->
      <div
        v-else-if="extractionState === 'running'"
        key="in-progress"
        class="extraction-state-container"
      >
        <NSpin :size="16" />
        <span class="extraction-state-text loading-text">Extraction in progress</span>
      </div>

      <!-- Error: show error icon and text -->
      <div
        v-else-if="extractionState === 'failed'"
        key="error"
        class="extraction-state-container extraction-error"
      >
        <NTooltip v-if="errorMessage" trigger="hover" placement="top">
          <template #trigger>
            <div class="extraction-error-content">
              <NIcon :size="24" color="#e63946" class="error-icon">
                <IconAlert />
              </NIcon>
              <span class="error-text">Extraction error</span>
            </div>
          </template>
          <span>{{ errorMessage }}</span>
        </NTooltip>
        <div v-else class="extraction-error-content">
          <NIcon :size="24" color="#e63946" class="error-icon">
            <IconAlert />
          </NIcon>
          <span class="error-text">Error</span>
        </div>
      </div>

      <!-- Done: show the value and optional human override icon -->
      <div
        v-else-if="extractionState === 'done'"
        key="done"
        class="extraction-state-container extraction-done extraction-with-override"
      >
        <span class="extraction-value">{{ displayValue || '' }}</span>
        <NTooltip v-if="hasHumanOverrideValue" trigger="hover" placement="top">
          <template #trigger>
            <NIcon :size="14" color="#2563eb" class="override-icon" aria-hidden="true">
              <IconUser />
            </NIcon>
          </template>
          <span>Manually overwritten</span>
        </NTooltip>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.extraction-cell-state {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  min-height: 24px;
}

.extraction-state-container {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 24px;
  padding: 2px 0;
}

.extraction-state-text {
  font-size: 12px;
  color: var(--n-text-color-2, #8e8e93);
  white-space: nowrap;
}

.loading-text {
  animation: pulse-fade 2s ease-in-out infinite;
}

@keyframes pulse-fade {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.extraction-error-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-icon {
  flex-shrink: 0;
}

.error-text {
  font-size: 12px;
  color: #e63946;
  font-weight: 500;
  white-space: nowrap;
}

.extraction-done {
  width: 100%;
}

/* Fill the cell so the override icon is at bottom-right of the cell */
.extraction-with-override {
  position: relative;
  width: 100%;
  min-height: 24px;
  padding-right: 18px;
  padding-bottom: 16px;
  box-sizing: border-box;
  align-self: stretch; /* fill cell height so absolute icon is at cell bottom */
}

.override-icon {
  position: absolute;
  bottom: 2px;
  right: 2px;
  flex-shrink: 0;
}

.extraction-value {
  width: 100%;
  word-break: break-word;
}

/* Smooth transitions between states */
.fade-enter-active,
.fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(-2px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(2px);
}
</style>
